import React, { useState, useCallback } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { firebaseApp } from "../../../providers/firebase";
import { firestoreTimestampToDate_event } from "../utils";
import Wysiwyg from "../../custom-ui/old-ui/Wysiwyg";
import { createEventSchema } from "./schema";
import { createNewEntryForEvents, updateEntryForEvents, useGetEventById } from "../../../data/event";
import toast from "react-hot-toast";
import { useAuth } from "../../../providers/auth";
import { Timestamp } from "firebase/firestore";
import ImageUploadEvent from "./ImageUpload_Event";
import { useEffect } from "react";
import ToggleButton from "./Toogle";
import DateGeneric from "../../custom-ui/old-ui/DateGeneric";

const storage = getStorage(firebaseApp);

export default function CreateEventForm({ toggleModal, id = "" }: { toggleModal: () => void, id?: string }) {

    const { user }: any = useAuth();
    const { data: formdata }: any = useGetEventById(id)

    const [selectedImages, setSelectedImages] = useState([]);
    const [previewImages, setPreviewImages] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [eventData, setEventData] = useState()
    const [existImages, setExistImages] = useState()

    const {
        control,
        register,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    }: any = useForm({
        resolver: zodResolver(createEventSchema),
        defaultValues: {
            title: "",
            status: "active",
            featured: "featured",
            eventStartDate: "",
            eventEndDate: "",
            eventStartTime: "",
            eventUrl: "",
            eventLocation: "",
            description: formdata?.description || eventData?.description || "",
        },
    });



    useEffect(() => {
        if (formdata) {
            reset({
                title: formdata.title || "",
                status: formdata.status || "active",
                eventStartDate: firestoreTimestampToDate_event(formdata?.eventStartDate || ""),
                eventEndDate: firestoreTimestampToDate_event(formdata?.eventEndDate || ""),
                eventStartTime: formdata?.eventStartTime,
                featured: formdata.featured || "featured",
                eventUrl: formdata.eventUrl || "",
                eventLocation: formdata.eventLocation || "",
                description: formdata.description || "",
            });

            setEventData(formdata);
        }
    }, [formdata, reset]);

    const startDateValue = watch("eventStartDate");

    const handleFiles = (files: FileList | null) => {
        if (!files) return;

        const newFiles = Array.from(files).filter((file) =>
            file.type.startsWith("image/")
        );

        if (selectedImages?.length + newFiles?.length + existImages?.length > 5) {
            alert("Maximum 5 images allowed");
            return;
        }

        setSelectedImages((prev: any) => [...prev, ...newFiles]);

        const newPreviewURLs = newFiles.map((file) => URL.createObjectURL(file));
        setPreviewImages((prev: any) => [...prev, ...newPreviewURLs]);
    };

    const onDrop = useCallback(
        (e: any) => {
            e.preventDefault();
            handleFiles(e.dataTransfer.files);
        },
        [selectedImages]
    );

    const onDragOver = (e: any) => e.preventDefault();


    const removeImage = (index: number) => {
        setSelectedImages((prev: any) => prev.filter((_: any, i: any) => i !== index));

        URL.revokeObjectURL(previewImages[index]);
        setPreviewImages((prev: any) => prev.filter((_: any, i: any) => i !== index));
    };

    const handleClearAll = () => {
        previewImages.forEach((url: any) => URL.revokeObjectURL(url));

        setSelectedImages([]);
        setPreviewImages([]);
        setEventData()
        reset({
            title: "",
            status: "active",
            featured: "featured",
            eventStartDate: "",
            eventEndDate: "",
            eventStartTime: "",
            eventUrl: "",
            eventLocation: "",
            description: "",
        });
    };


    const onSubmit = async (data: any) => {

        setIsUploading(true);

        let uploadedUrls: string[] = [];

        if (selectedImages.length > 0) {
            uploadedUrls = await Promise.all(
                selectedImages.map(async (file: any) => {
                    const fileRef = ref(storage, `events/${Date.now()}_${file.name}`);
                    const snap = await uploadBytes(fileRef, file);
                    const url = await getDownloadURL(snap.ref);
                    return url;
                })
            );
        }

        const finalData = {
            ...data,
            eventEndDate: data?.eventEndDate || null,

            images: [
                ...(existImages || []),
                ...uploadedUrls,
            ],
        };

        const eventPayload = {
            ...finalData,
            autor: user?.name || user?.auth?.displayName,
            created_by: user?.name || user?.auth?.displayName,
            updated_at: Timestamp.now(),
            updated_by: user?.name || user?.auth?.displayName,
            isFeatuered: data.featured === "featured" ? true : false,
            post_type: "event",
        };


        if (id) {
            updateEntryForEvents(id, eventPayload).then(() => {
                toggleModal();
                toast.success("Event Updated successfully!");

            }).catch((error) => {
                setIsUploading(false);
                toast.error("Failed to update post.");
            });

        } else {
            createNewEntryForEvents(eventPayload).then(() => {
                toggleModal();
                toast.success("Event created successfully!");

            }).catch((error) => {
                setIsUploading(false);
                toast.error("Failed to create post.");
            });
        }
    };




    return (
        <div className="flex justify-center items-center min-h-screen px-4">
            <div className="p-4 rounded-lg w-full max-w-3xl">
                <h2 className="text-[#1a3b5d] text-xl font-bold  mb-4">{ id?"Edit Event":"Create New Event"}</h2>
                {formdata?.source === "eventbrite" && (
                    <div className="mb-4 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
                        This event is synced from Eventbrite. Core details update automatically when Eventbrite changes.
                        You can still archive or feature it from the event card menu.
                    </div>
                )}
                <div className="h-0.5 bg-green-700 mb-6"></div>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">

                    {/* Title */}
                    <div>
                        <label className="text-xs text-[#1a3b5d] font-semibold">Event Title *</label>
                        <Controller
                            control={control}
                            name="title"
                            render={(field) => (
                                <input
                                    type="text"
                                    className="w-full h-12 border-b border-gray-300 focus:border-[#1a3b5d] outline-none py-2 text-gray-700 text-lg placeholder-gray-400 transition-colors"
                                    placeholder="Enter Event Title"
                                    {...field}
                                />
                            )}
                        />
                        {errors?.title && <p className="text-red-500 text-sm">{errors.title.message}</p>}
                    </div>



                    {/* Event Dates */}
                    <div className="grid grid-cols-1 md:grid-cols-2 ">

                        <div>
                            <label className="text-xs text-[#1a3b5d] font-semibold">Start Date *</label>
                            <Controller
                                control={control}
                                name="eventStartDate"
                                render={(field) => (
                                    <DateGeneric
                                        {...field}
                                        display="Start Date"
                                        timestamp={true}
                                        readOnly={false}
                                        error={errors?.eventStartDate?.message}
                                        required
                                    />
                                )}
                            />
                        </div>

                       { startDateValue &&<div className="mb-[-15px]" >
                            <label className="text-xs text-[#1a3b5d] font-semibold">End Date (Optional)</label>



                            <Controller
                                control={control}
                                name="eventEndDate"
                                render={(field) => (
                                    <DateGeneric
                                        {...field}
                                        display="End Date"
                                        timestamp={true}
                                        readOnly={false}
                                        minDate={startDateValue}
                                        error={errors?.eventEndDate?.message}
                                    />
                                )}
                            />

                        </div>}

                    </div>

                    {/* Event Time */}
                    <div>
                        <label className="text-xs text-[#1a3b5d] font-semibold">Event Start Time (Optional)</label>
                        <Controller control={control}
                            name="eventStartTime"
                            render={(field) => (
                                <input
                                    type="time"
                                    className="w-full h-12 border-b border-gray-300 focus:border-[#1a3b5d] outline-none py-2 text-gray-700 text-lg placeholder-gray-400 transition-colors"
                                    placeholder="Pick a date"
                                    {...field}
                                />)}
                            error={errors?.eventStartTime?.message} required />
                        {errors.eventStartTime && (
                            <p className="text-red-500 text-sm">{errors.eventStartTime.message}</p>
                        )}

                    </div>

                    {/* Event URL */}
                    <div>
                        <label className="text-xs text-[#1a3b5d] font-semibold">Event URL *</label>

                        <Controller control={control}
                            name="eventUrl"
                            render={(field) => (
                                <input type="text"
                                    className="w-full h-12 border-b border-gray-300 focus:border-[#1a3b5d] outline-none py-2 text-gray-700 text-lg placeholder-gray-400 transition-colors"
                                    placeholder="Enter Event URL"
                                    {...field}
                                />)}
                            error={errors?.eventUrl?.message} required />
                        {errors.eventUrl && (
                            <p className="text-red-500 text-sm">{errors.eventUrl.message}</p>
                        )}

                    </div>

                    {/* Event Location */}
                    <div>
                        <label className="text-xs text-[#1a3b5d] font-semibold">Location *</label>
                        <Controller control={control}
                            name="eventLocation"
                            render={(field) => (
                                <input type="text"
                                    className="w-full h-12 border-b border-gray-300 focus:border-[#1a3b5d] outline-none py-2 text-gray-700 text-lg placeholder-gray-400 transition-colors"
                                    placeholder="Enter Event Location"
                                    {...field}
                                />)}
                            error={errors?.eventLocation?.message} required />
                        {errors.eventLocation && (
                            <p className="text-red-500 text-sm">{errors.eventLocation.message}</p>
                        )}


                    </div>

                    {/* Status */}
                    <div>
                        <h3 className="text-[#1a3b5d] font-bold">Status</h3>
                        <div className="h-0.5 bg-green-700 mb-4"></div>
                        <Controller
                            control={control}
                            name="status"
                            render={(field) => (
                                <ToggleButton
                                    isOn={field.value === "active"}
                                    buttonText={field.value === "active" ? "Active" : "Archived"}
                                    handleClick={() => {
                                        field.onChange(field.value === "active" ? "archived" : "active");
                                    }}
                                />
                            )}
                        />
                    </div>

                    {/* Featured */}
                    <div>
                        <h3 className="text-[#1a3b5d] font-bold">Featured</h3>
                        <div className="h-0.5 bg-green-700 mb-4"></div>
                        <Controller
                            control={control}
                            name="featured"
                            render={(field) => (
                                <div className="flex items-center gap-3">
                                    <ToggleButton
                                        isOn={field.value === "featured"}
                                        buttonText={field.value === "featured" ? "Featured" : "Non-Featured"}
                                        handleClick={() =>
                                            field.onChange(field.value === "featured" ? "non-featured" : "featured")
                                        }
                                    />
                                </div>
                            )}
                        />
                    </div>

                    {/* Description */}
                    <div>
                        <label className="text-[#1a3b5d] font-bold">Description *</label>
                        <Controller
                            control={control}
                            name="description"
                            render={(field) => (
                                <Wysiwyg
                                    {...field}
                                    rows={10}
                                    value={field?.value}
                                    readOnly={false}
                                    placeholder="Write event details"
                                    directions=""
                                    editorStyle={{ height: "180px" }}
                                    error={errors?.description?.message}
                                />
                            )}
                        />

                    </div>

                    <div
                        className="border-2 border-dashed rounded-lg p-6"
                        onDrop={onDrop}
                        onDragOver={onDragOver}
                    >
                        <ImageUploadEvent
                            previewImages={previewImages}
                            handleFiles={handleFiles}
                            removeImage={removeImage}
                            selectedImages={selectedImages}
                            uploadedImges={eventData?.images || []}
                            setExistImages={setExistImages}
                        />

                    </div>
                    {/* Image Upload */}


                    {/* BUTTONS */}
                    <div className="flex justify-center gap-6 mt-6">
                      { !id&& <button
                            type="button"
                            onClick={handleClearAll}
                            className="px-10 py-2.5 rounded-full border border-[#1a3b5d] text-[#1a3b5d]"
                        >
                            Clear All
                        </button>}

                        <button
                            type="submit"
                            disabled={isUploading}
                            className="px-10 py-2.5 rounded-full bg-[#1a3b5d] text-white"
                        >
                            {isUploading ? "Uploading..." : "Save Event"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
